import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { customerLabel, dateOnly, timeOnly, vehicleLabel } from "../lib/format";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Layout";
import {
  Button,
  Card,
  DataState,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Select,
  StatusBadge,
  Table,
} from "../components/ui";
import type { Customer, RepairOrder, Vehicle } from "../lib/types";

type Appointment = {
  id: string;
  scheduledFor: string;
  estimatedHours: string;
  concern: string;
  requestedWork: string | null;
  mileageIn: number | null;
  status: string;
  customer: Customer;
  vehicle: Vehicle;
  repairOrder: { id: string; number: string; status: string } | null;
};

function isoDay(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

/**
 * Step 1 of the workflow: book and check in (build guide / 04).
 *
 * Check-in creates the repair order and carries the customer, vehicle, mileage
 * and concern across. Nothing is re-entered.
 */
export function AppointmentsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState(isoDay(0));
  const [to, setTo] = useState(isoDay(7));
  const [booking, setBooking] = useState(false);

  const appointments = useQuery({
    queryKey: ["appointments", from, to],
    queryFn: () =>
      api.get<{ data: Appointment[] }>(
        `/appointments?from=${new Date(from).toISOString()}&to=${new Date(`${to}T23:59:59`).toISOString()}`,
      ),
  });

  const checkIn = useMutation({
    mutationFn: (vars: { id: string; mileageIn?: number }) =>
      api.post<RepairOrder>(`/appointments/${vars.id}/check-in`, {
        mileageIn: vars.mileageIn,
      }),
    onSuccess: (ro) => {
      void queryClient.invalidateQueries({ queryKey: ["appointments"] });
      void queryClient.invalidateQueries({ queryKey: ["board"] });
      navigate(`/jobs/${ro.id}`);
    },
  });

  // Group by calendar day so the advisor reads a schedule, not a flat list.
  const byDay = new Map<string, Appointment[]>();
  for (const appt of appointments.data?.data ?? []) {
    const key = appt.scheduledFor.slice(0, 10);
    byDay.set(key, [...(byDay.get(key) ?? []), appt]);
  }

  return (
    <>
      <PageHeader
        title="Appointments"
        description="Booked work. Checking in creates the repair order and records the mileage."
        action={
          can("appointments.write") ? (
            <Button onClick={() => setBooking(true)}>Book appointment</Button>
          ) : undefined
        }
      />

      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <Field label="From">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Button
            variant="secondary"
            onClick={() => {
              setFrom(isoDay(0));
              setTo(isoDay(0));
            }}
          >
            Today only
          </Button>
        </div>

        <ErrorBanner error={checkIn.error} />

        <DataState
          loading={appointments.isLoading}
          error={appointments.error}
          empty={(appointments.data?.data.length ?? 0) === 0}
          emptyMessage="Nothing booked in this range."
        >
          <div className="space-y-5">
            {[...byDay.entries()].map(([day, items]) => (
              <div key={day}>
                <h2 className="mb-2 text-sm font-semibold text-ink">{dateOnly(day)}</h2>
                <Table
                  headers={["Time", "Customer", "Vehicle", "Concern", "Est.", "Status", ""]}
                >
                  {items.map((appt) => (
                    <tr key={appt.id}>
                      <td className="whitespace-nowrap px-3 py-2 font-medium tabular-nums">
                        {timeOnly(appt.scheduledFor)}
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/customers/${appt.customer.id}`}
                          className="hover:text-teal-700"
                        >
                          {customerLabel(appt.customer)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{vehicleLabel(appt.vehicle)}</td>
                      <td className="max-w-xs truncate px-3 py-2 text-slate-600">
                        {appt.concern}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-600">
                        {Number(appt.estimatedHours).toFixed(1)}h
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={appt.status} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        {appt.repairOrder ? (
                          <Link
                            to={`/jobs/${appt.repairOrder.id}`}
                            className="text-sm text-teal-700 hover:underline"
                          >
                            {appt.repairOrder.number}
                          </Link>
                        ) : (
                          can("jobs.write") && (
                            <Button
                              variant="secondary"
                              loading={checkIn.isPending}
                              onClick={() =>
                                checkIn.mutate({
                                  id: appt.id,
                                  mileageIn: appt.mileageIn ?? undefined,
                                })
                              }
                            >
                              Check in
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </Table>
              </div>
            ))}
          </div>
        </DataState>
      </Card>

      {booking && <BookModal onClose={() => setBooking(false)} />}
    </>
  );
}

function BookModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [customerId, setCustomerId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    scheduledFor: `${isoDay(1)}T09:00`,
    estimatedHours: "1",
    concern: "",
    requestedWork: "",
    mileageIn: "",
  });

  const customers = useQuery({
    queryKey: ["customers", search],
    queryFn: () => api.get<{ data: Customer[] }>(`/customers?q=${encodeURIComponent(search)}`),
  });

  // Vehicles are scoped to the chosen customer, so a job cannot be booked
  // against someone else's car.
  const vehicles = useQuery({
    queryKey: ["vehicles", customerId],
    queryFn: () => api.get<{ data: Vehicle[] }>(`/vehicles?customerId=${customerId}`),
    enabled: Boolean(customerId),
  });

  const book = useMutation({
    mutationFn: () =>
      api.post("/appointments", {
        customerId,
        vehicleId,
        scheduledFor: new Date(form.scheduledFor).toISOString(),
        estimatedHours: Number(form.estimatedHours || 1),
        concern: form.concern,
        requestedWork: form.requestedWork || null,
        mileageIn: form.mileageIn ? Number(form.mileageIn) : null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["appointments"] });
      onClose();
    },
  });

  return (
    <Modal title="Book appointment" onClose={onClose} wide>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          book.mutate();
        }}
      >
        <ErrorBanner error={book.error} />

        <Field label="Find customer">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, phone or email"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Customer" required>
            <Select
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                setVehicleId("");
              }}
              required
            >
              <option value="">Select a customer…</option>
              {customers.data?.data.map((c) => (
                <option key={c.id} value={c.id}>
                  {customerLabel(c)}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Vehicle"
            required
            hint={
              customerId && vehicles.data?.data.length === 0
                ? "This customer has no vehicle on file yet."
                : undefined
            }
          >
            <Select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              disabled={!customerId}
              required
            >
              <option value="">Select a vehicle…</option>
              {vehicles.data?.data.map((v) => (
                <option key={v.id} value={v.id}>
                  {vehicleLabel(v)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Field label="Date and time" required>
              <Input
                type="datetime-local"
                value={form.scheduledFor}
                onChange={(e) => setForm((f) => ({ ...f, scheduledFor: e.target.value }))}
                required
              />
            </Field>
          </div>
          <Field label="Estimated hours">
            <Input
              value={form.estimatedHours}
              onChange={(e) => setForm((f) => ({ ...f, estimatedHours: e.target.value }))}
              inputMode="decimal"
            />
          </Field>
        </div>

        <Field label="Customer concern" required hint="In the customer's own words.">
          <Input
            value={form.concern}
            onChange={(e) => setForm((f) => ({ ...f, concern: e.target.value }))}
            required
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Requested work">
            <Input
              value={form.requestedWork}
              onChange={(e) => setForm((f) => ({ ...f, requestedWork: e.target.value }))}
              placeholder="What they asked for, if different"
            />
          </Field>
          <Field label="Mileage">
            <Input
              value={form.mileageIn}
              onChange={(e) => setForm((f) => ({ ...f, mileageIn: e.target.value }))}
              inputMode="numeric"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={book.isPending} disabled={!customerId || !vehicleId}>
            Book appointment
          </Button>
        </div>
      </form>
    </Modal>
  );
}
